import axios from "axios";
import { useTranslation } from "react-i18next";
import { markExpiryHandled } from "services/files-category.service";
import { vilniusTodayYmd, vilniusYmd } from "utils/vilnius-date";
import { Translate } from "./task-row";

/**
 * How a document's end date reads on «Baigiasi galiojimas»: "Expired <date>" before today,
 * else "Valid until <date>" (Vilnius calendar days).
 */
export function expiryStatus(dateTo: string, t: Translate) {
  const date = vilniusYmd(dateTo);
  const isExpired = date < vilniusTodayYmd();
  return { isExpired, label: isExpired ? t("tasks_expired_on", { date }) : t("tasks_valid_until", { date }) };
}

/** What one row says about its document: its id, its own company and the end date the row shows. */
export interface ExpiringDocument {
  fileId: string;
  companyId: string;
  dateTo: string;
}

/**
 * «Sutvarkyta» on an expiring document: asks first, then marks it for the end date the row
 * showed and calls `reload` for the list the row was on. When the document changed meanwhile
 * (another date, reassigned, annulled — 409) it says so and reloads; any other failure says so
 * and leaves the row.
 */
export function useMarkExpiryHandled(reload: () => void) {
  const { t } = useTranslation();
  return async ({ fileId, companyId, dateTo }: ExpiringDocument) => {
    if (!window.confirm(t("expiry_handled_confirm"))) return;
    try {
      await markExpiryHandled(companyId, fileId, dateTo);
      reload();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        window.alert(t("expiry_handled_changed"));
        reload();
        return;
      }
      window.alert(t("expiry_handled_failed"));
    }
  };
}
